import { gsap } from 'gsap'
import { getSceneImagePath } from '../../../utils/pathResolver'
import { SaveStateManager } from '../../../features/save-state'
import { ITEM_NAMES, ITEMS_NEEDED_TO_PASS } from './constants'
import { getAudioManager } from '../../../features/audio'
import {
  getGlobalScrollIndicator,
  showGlobalScrollIndicator,
  hideGlobalScrollIndicator,
} from '../../../utils/scroll-indicator'
import { createConfirmationButtonElement, removeConfirmationButtonElement } from '../../scenes/choices/choice-manager'
import type { SceneData } from '../../scenes/types'
import track, { SET_INVENTORY_ITEMS } from '../../analytics'
import './BaleniSRychlonozkou.css'

const SCENE_ID = 'minihra-baleni-s-rychlonozkou'

export interface State {
  selectedItems: string[]
  completed: boolean
}

function loadState(): State {
  const saved = SaveStateManager.load()
  const minigame = saved?.minigames?.[SCENE_ID]
  if (minigame) {
    // Support both old format (selected/confirmed) and new format (selectedItems/completed)
    const selectedItems = (minigame.selectedItems ?? minigame.selected ?? []).slice(0, ITEMS_NEEDED_TO_PASS)
    const completed = minigame.completed ?? minigame.confirmed ?? false
    return { selectedItems, completed }
  }
  return { selectedItems: [], completed: false }
}

function saveState(state: State): void {
  const saved = SaveStateManager.load() ?? SaveStateManager.createInitialState()
  if (!saved.minigames) saved.minigames = {}
  // Create a new object to ensure we're saving the current state
  saved.minigames[SCENE_ID] = {
    selectedItems: [...state.selectedItems], // Create a copy to ensure immutability
    completed: state.completed,
  }
  SaveStateManager.saveImmediate(saved)
}

/**
 * Sync minigame selected items to inventory
 * This ensures conditional choices can check for items using the inventory condition.
 * Items in consumedInventory (removed by choice effects) are never re-added.
 */
function syncItemsToInventory(selectedItems: string[]): void {
  const saved = SaveStateManager.load() ?? SaveStateManager.createInitialState()
  if (!saved.inventory) saved.inventory = []
  const consumed = new Set(saved.consumedInventory ?? [])
  selectedItems.forEach((item) => {
    if (!consumed.has(item) && !saved.inventory!.includes(item)) {
      saved.inventory!.push(item)
    }
  })
  SaveStateManager.saveImmediate(saved)
}

export function createMinigame(container: HTMLElement, onComplete?: () => void, sceneData?: SceneData): HTMLElement {
  const root = document.createElement('div')
  root.className = 'minigame-baleni-s-rychlonozkou'
  root.setAttribute('data-minigame', SCENE_ID)

  let state = loadState()

  // If minigame was already completed, sync items to inventory
  if (state.completed && state.selectedItems.length > 0) {
    syncItemsToInventory(state.selectedItems)
  }

  const counterWrap = document.createElement('div')
  counterWrap.className = 'minigame-baleni-s-rychlonozkou__counter-wrap'
  const counterEl = document.createElement('div')
  counterEl.className = 'minigame-baleni-s-rychlonozkou__counter'
  counterEl.setAttribute('aria-live', 'polite')
  function updateCounter() {
    const count = state.selectedItems.length
    counterEl.textContent = `${count} / ${ITEMS_NEEDED_TO_PASS}`
  }
  updateCounter()
  counterWrap.appendChild(counterEl)
  root.appendChild(counterWrap)

  const tableWrap = document.createElement('div')
  tableWrap.className = 'minigame-baleni-s-rychlonozkou__table-wrap'

  const bgImg = document.createElement('img')
  bgImg.className = 'minigame-baleni-s-rychlonozkou__bg'
  bgImg.src = getSceneImagePath(SCENE_ID, 'bg.png')
  bgImg.alt = ''
  tableWrap.appendChild(bgImg)

  const itemsWrap = document.createElement('div')
  itemsWrap.className = 'minigame-baleni-s-rychlonozkou__items'
  tableWrap.appendChild(itemsWrap)

  root.appendChild(tableWrap)

  // Button elements (created but not appended until all items are selected)
  let buttonWrap: HTMLDivElement | null = null
  let button: HTMLButtonElement | null = null

  // Function to create button elements
  function createButton(): void {
    if (buttonWrap) return // Already created

    buttonWrap = document.createElement('div')
    buttonWrap.className = 'minigame-baleni-s-rychlonozkou__button-wrap'

    // Get button label from scene data or use default
    const buttonLabel = sceneData?.buttonLabel || 'Mám sbaleno'

    // Use the same confirmation button as choices
    button = createConfirmationButtonElement(() => {
      if (state.completed === true || state.selectedItems.length !== ITEMS_NEEDED_TO_PASS) return

      // Hide scroll indicator when button is clicked
      hideGlobalScrollIndicator()

      const audioManager = getAudioManager()
      audioManager.playCustomSound('batoh-zavreni.mp3', SCENE_ID)

      state.completed = true

      // Save both minigame state and inventory in a single atomic operation
      const saved = SaveStateManager.load() ?? SaveStateManager.createInitialState()
      if (!saved.minigames) saved.minigames = {}
      saved.minigames[SCENE_ID] = {
        selectedItems: [...state.selectedItems],
        completed: state.completed,
      }
      // Sync selected items to inventory (skip consumed items)
      if (!saved.inventory) saved.inventory = []
      const consumed = new Set(saved.consumedInventory ?? [])
      state.selectedItems.forEach((item) => {
        if (!consumed.has(item) && !saved.inventory!.includes(item)) {
          saved.inventory!.push(item)
        }
      })
      SaveStateManager.saveImmediate(saved)

      track(SET_INVENTORY_ITEMS, saved.inventory.join(', '))

      // Remove confirmation button with fade-out animation (same as choices)
      if (button) {
        gsap.to(button, {
          opacity: 0,
          duration: 0.2,
          ease: 'power2.in',
          onComplete: () => {
            // Remove button and wrap after animation completes
            if (button && button.parentNode) {
              button.remove()
            }
            if (buttonWrap && buttonWrap.parentNode) {
              buttonWrap.parentNode.removeChild(buttonWrap)
            }
            button = null
            buttonWrap = null
          },
        })
      }

      // Call onComplete callback to load next scene
      if (onComplete) {
        onComplete()
      }
    }, buttonLabel)

    buttonWrap.appendChild(button)

    // Animate button in (fade in only, no movement)
    gsap.set(button, { opacity: 0 })
    gsap.to(button, {
      opacity: 1,
      duration: 0.3,
      ease: 'power2.out',
    })
  }

  // Function to update button state (add/remove from DOM)
  function updateButtonState() {
    const allSelected = state.selectedItems.length === ITEMS_NEEDED_TO_PASS
    const isCompleted = state.completed === true

    if (allSelected && !isCompleted) {
      // All items selected and not yet completed - show button
      if (!buttonWrap || !button) {
        createButton()
      }
      if (buttonWrap && !buttonWrap.parentNode) {
        root.appendChild(buttonWrap)
      }
    } else {
      // Not all items selected OR already completed - remove button from DOM
      if (buttonWrap && buttonWrap.parentNode) {
        buttonWrap.parentNode.removeChild(buttonWrap)
        buttonWrap = null
        button = null
      }
    }
  }

  // Wrapper to update both counter and button state
  function updateCounterAndButton() {
    updateCounter()
    updateButtonState()
    updateScrollIndicator()
  }

  // Use global scroll indicator (shared across all scenes)
  const scrollIndicator = getGlobalScrollIndicator()

  // Function to check if button is mostly visible
  function isButtonMostlyVisible(): boolean {
    if (!buttonWrap) return false
    const rect = buttonWrap.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    // Check if button is mostly visible (at least 50% of its height is in viewport)
    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
    return visibleHeight >= rect.height * 0.5
  }

  // Function to check if scrolled to bottom
  function isAtBottom(): boolean {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const scrollHeight = document.documentElement.scrollHeight
    const clientHeight = document.documentElement.clientHeight
    // Consider at bottom if within 100px of bottom
    return scrollHeight - scrollTop - clientHeight < 100
  }

  // Function to update scroll indicator visibility
  function updateScrollIndicator(): void {
    // Once completed, stop managing the indicator so other minigames can use it
    if (state.completed) return

    const saved = SaveStateManager.load()
    const savedMinigame = saved?.minigames?.[SCENE_ID]
    if (savedMinigame?.completed === true) return

    const hasAllItems = state.selectedItems.length === ITEMS_NEEDED_TO_PASS
    const buttonVisible = isButtonMostlyVisible()
    const atBottom = isAtBottom()

    if (hasAllItems && !buttonVisible && !atBottom) {
      showGlobalScrollIndicator()
    } else {
      hideGlobalScrollIndicator()
    }
  }

  // Set up scroll listener to update indicator
  const handleScroll = () => {
    updateScrollIndicator()
  }
  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('resize', handleScroll, { passive: true })

  ITEM_NAMES.forEach((itemId, index) => {
    const row = document.createElement('div')
    row.className = 'minigame-baleni-s-rychlonozkou__row'
    const isOdd = index % 2 === 0

    // Create button element for accessibility
    const itemButton = document.createElement('button')
    itemButton.className = 'minigame-baleni-s-rychlonozkou__item-button'
    itemButton.type = 'button'
    itemButton.setAttribute('data-item-id', itemId)
    itemButton.setAttribute('aria-label', `Vybrat ${itemId}`)
    itemButton.setAttribute('aria-pressed', String(state.selectedItems.includes(itemId)))

    // Disable button if minigame is completed
    if (state.completed === true) {
      itemButton.disabled = true
      itemButton.setAttribute('aria-disabled', 'true')
    }

    const itemImg = document.createElement('img')
    itemImg.className = 'minigame-baleni-s-rychlonozkou__item-img'
    itemImg.src = getSceneImagePath(SCENE_ID, `items/${itemId}.png`)
    itemImg.alt = itemId
    itemButton.appendChild(itemImg)

    const itemOutlineImg = document.createElement('img')
    itemOutlineImg.className = 'minigame-baleni-s-rychlonozkou__item-img-outline'
    itemOutlineImg.src = getSceneImagePath(SCENE_ID, `items/${itemId}-outline.png`)
    itemOutlineImg.alt = itemId
    itemButton.appendChild(itemOutlineImg)

    // Create button wrapper for bubble to make it clickable
    const bubbleButton = document.createElement('button')
    bubbleButton.className = 'minigame-baleni-s-rychlonozkou__bubble-button'
    bubbleButton.type = 'button'
    bubbleButton.disabled = true
    bubbleButton.setAttribute('aria-label', `Přehrát zvuk pro ${itemId}`)
    bubbleButton.style.background = 'none'
    bubbleButton.style.border = 'none'
    bubbleButton.style.padding = '0'
    bubbleButton.style.cursor = 'pointer'
    bubbleButton.style.display = 'inline-block'

    const bubbleImg = document.createElement('img')
    bubbleImg.className = 'minigame-baleni-s-rychlonozkou__bubble-img'
    bubbleImg.src = getSceneImagePath(SCENE_ID, `items/bubble-${itemId}.png`)
    bubbleImg.alt = ''
    bubbleImg.style.pointerEvents = 'none' // Prevent image from intercepting clicks
    // Initially set scale to 0 for pop-out animation (same as speech bubbles)
    gsap.set(bubbleButton, { scale: 0, transformOrigin: 'center center' })

    bubbleButton.appendChild(bubbleImg)

    // Make bubble clickable to replay sound (same as regular speech bubbles)
    // Only enable interactions when bubble is visible (scale > 0)
    bubbleButton.addEventListener('mouseenter', () => {
      // Only apply hover effect if bubble is visible (selected)
      if (state.selectedItems.includes(itemId)) {
        gsap.to(bubbleButton, { scale: 0.95, duration: 0.2, ease: 'power2.out', overwrite: true })
      }
    })
    bubbleButton.addEventListener('mouseleave', () => {
      // Only apply hover effect if bubble is visible (selected)
      if (state.selectedItems.includes(itemId)) {
        gsap.to(bubbleButton, { scale: 1, duration: 0.2, ease: 'power2.out', overwrite: true })
      }
    })
    bubbleButton.addEventListener('click', () => {
      // Only allow clicks when bubble is visible (selected)
      if (!state.selectedItems.includes(itemId)) return

      // Play bubble sound when clicked (same as when it first appears)
      const audioManager = getAudioManager()
      audioManager.playBubbleSoundByName(itemId, SCENE_ID)
      // Click animation: scale down then bounce back
      gsap.to(bubbleButton, { scale: 0.8, duration: 0.05, ease: 'power2.out', overwrite: true })
      gsap.to(bubbleButton, { scale: 1, duration: 0.12, delay: 0.05, ease: 'back.out(1.5)', overwrite: true })
    })

    const left = document.createElement('div')
    left.className = 'minigame-baleni-s-rychlonozkou__slot'
    const right = document.createElement('div')
    right.className = 'minigame-baleni-s-rychlonozkou__slot'
    if (isOdd) {
      left.appendChild(itemButton)
      right.appendChild(bubbleButton)
    } else {
      right.appendChild(itemButton)
      left.appendChild(bubbleButton)
    }
    row.appendChild(left)
    row.appendChild(right)
    itemsWrap.appendChild(row)

    function animateBubblePopOut(bubbleButton: HTMLButtonElement): void {
      // Comic-style pop-out animation with bounce (same as speech bubbles)
      gsap.to(bubbleButton, {
        scale: 1,
        duration: 0.4,
        ease: 'back.out(1.7)', // Elastic bounce effect
      })
    }

    function animateBubblePopIn(bubbleButton: HTMLButtonElement): void {
      // Reverse animation - pop in (scale to 0)
      gsap.to(bubbleButton, {
        scale: 0,
        duration: 0.4,
        ease: 'back.in(1.7)', // Reverse bounce effect
      })
    }

    function setSelected(selected: boolean, animate: boolean = true) {
      itemButton.classList.toggle('minigame-baleni-s-rychlonozkou__item-button--selected', selected)
      itemButton.setAttribute('aria-pressed', String(selected))
      itemImg.classList.toggle('minigame-baleni-s-rychlonozkou__item-img--selected', selected)

      bubbleButton.disabled = !selected

      if (selected) {
        if (animate) {
          animateBubblePopOut(bubbleButton)
          const audioManager = getAudioManager()
          audioManager.playBubbleSoundByName(itemId, SCENE_ID)
        } else {
          gsap.set(bubbleButton, { scale: 1 })
        }
      } else {
        if (animate) {
          animateBubblePopIn(bubbleButton)
        } else {
          gsap.set(bubbleButton, { scale: 0 })
        }
      }
    }

    // Set initial state (no animation for saved state)
    const isInitiallySelected = state.selectedItems.includes(itemId)
    setSelected(isInitiallySelected, false)

    // Handle button click
    itemButton.addEventListener('click', () => {
      if (state.completed === true || itemButton.disabled) return // Prevent changes if completed
      const idx = state.selectedItems.indexOf(itemId)
      if (idx >= 0) {
        state.selectedItems = state.selectedItems.filter((id) => id !== itemId)
      } else if (state.selectedItems.length < ITEMS_NEEDED_TO_PASS) {
        state.selectedItems = [...state.selectedItems, itemId]
      }
      // Save state immediately after updating
      saveState(state)
      setSelected(state.selectedItems.includes(itemId))
      updateCounterAndButton()
    })

    // Handle keyboard (Enter and Space are handled by default button behavior, but we can add explicit handling)
    itemButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        itemButton.click()
      }
    })
  })

  // Update button state initially
  updateButtonState()

  // Update scroll indicator initially
  updateScrollIndicator()

  // Store cleanup function on the root element for scene cleanup
  ;(root as any).__minigameCleanup = () => {
    window.removeEventListener('scroll', handleScroll)
    window.removeEventListener('resize', handleScroll)
    // Don't remove the global scroll indicator - it's shared across all scenes
    hideGlobalScrollIndicator()
  }

  return root
}
